import { useCallback, useEffect, useMemo, useState } from 'react';
import { NONE_SIGNATURE_OPTION } from '@/components/Drawers/AddUnlayerEmailDrawer/constants';
import {
	getSignatureOptionMatchKey,
	normalizeSignatureEmail,
} from '@/features/unlayerUserSignatures/helpers';

/**
 * Owns the compose selector's automatic and manual selection rules independently
 * from the data-loading and insertion-request mechanics.
 */
export const useSignatureSelectionState = ({
	activeSignatureOptions,
	programmaticDesignLoadRevision,
	queueSignatureInsertion,
	selectedFrom,
	signatureOptions,
}) => {
	const [selectionMode, setSelectionMode] = useState('auto');
	const [selectedSignature, setSelectedSignature] = useState(NONE_SIGNATURE_OPTION);
	const selectedFromEmail = normalizeSignatureEmail(
		selectedFrom?.value ?? selectedFrom?.email ?? selectedFrom?.name
	);
	const signatureOptionsByEmail = useMemo(
		() =>
			new Map(
				(activeSignatureOptions ?? []).map((option) => [option.normalizedEmail, option])
			),
		[activeSignatureOptions]
	);
	const findOptionByEmail = useCallback(
		(email) => signatureOptionsByEmail.get(normalizeSignatureEmail(email)) ?? null,
		[signatureOptionsByEmail]
	);
	const applySelection = useCallback(
		async (signatureOption, mode = 'manual') => {
			const nextOption = signatureOption ?? NONE_SIGNATURE_OPTION;
			const currentKey = getSignatureOptionMatchKey(selectedSignature);
			const nextKey = getSignatureOptionMatchKey(nextOption);
			if (currentKey === nextKey) {
				return;
			}

			setSelectionMode(mode);
			setSelectedSignature(nextOption);
			await queueSignatureInsertion(nextOption);
		},
		[queueSignatureInsertion, selectedSignature]
	);
	const handleSignatureSelect = useCallback(
		async (signatureOption) => {
			await applySelection(signatureOption, 'manual');
		},
		[applySelection]
	);

	// Refresh selection validity when the available source changes so the selector
	// can preserve manual choices when valid and fall back to auto-selection rules
	// when the current option disappears.
	useEffect(() => {
		const selectedKey = getSignatureOptionMatchKey(selectedSignature);
		const stillValidSelection = signatureOptions.find(
			(option) => getSignatureOptionMatchKey(option) === selectedKey
		);

		if (selectionMode === 'manual' && stillValidSelection) {
			if (stillValidSelection !== selectedSignature) {
				setSelectedSignature(stillValidSelection);
			}
			return;
		}

		if (selectionMode === 'manual' && !stillValidSelection) {
			const autoMatchedOption = findOptionByEmail(selectedFromEmail);
			if (autoMatchedOption) {
				applySelection(autoMatchedOption, 'auto');
				return;
			}
			applySelection(NONE_SIGNATURE_OPTION, 'auto');
			return;
		}

		const autoMatchedOption = findOptionByEmail(selectedFromEmail);
		if (autoMatchedOption) {
			applySelection(autoMatchedOption, 'auto');
			return;
		}

		if (selectedSignature.id !== NONE_SIGNATURE_OPTION.id) {
			applySelection(NONE_SIGNATURE_OPTION, 'auto');
		}
	}, [
		applySelection,
		findOptionByEmail,
		selectedFromEmail,
		selectedSignature,
		selectionMode,
		signatureOptions,
	]);

	// Template and draft loads replace the editor contents, so once a programmatic
	// load completes we need to reapply the currently selected signature against
	// the loaded design rather than the pre-load snapshot.
	useEffect(() => {
		if (programmaticDesignLoadRevision === 0) {
			return;
		}
		if (!selectedSignature || selectedSignature.id === NONE_SIGNATURE_OPTION.id) {
			return;
		}

		queueSignatureInsertion(selectedSignature);
	}, [programmaticDesignLoadRevision, queueSignatureInsertion, selectedSignature]);

	return {
		handleSignatureSelect,
		selectedSignature,
	};
};
